import BigNumber from 'bignumber.js';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { PropsWithChildren } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { flex, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { UsdtCurrency } from '@flipster/utils/constants/currency';

import milestoneLIcon from './assets/milestone_l.webp';
import milestoneMIcon from './assets/milestone_m.webp';
import milestoneSIcon from './assets/milestone_s.webp';
import { milestoneBunja } from './state';

export const GaugeArea = () => {
  return (
    <div className={stack({ gap: 16, alignItems: 'stretch' })}>
      <Top />
      <Gauge />
      <Bottom />
    </div>
  );
};

const Top = () => {
  const { milestonesAtom, currMilestoneIndexAtom, isFinalStageAtom, isUpcomingAtom } = useBunja(milestoneBunja);
  const milestones = useAtomValue(milestonesAtom);
  const currMilestoneIndex = useAtomValue(currMilestoneIndexAtom);
  const isFinalStage = useAtomValue(isFinalStageAtom);
  const isUpcoming = useAtomValue(isUpcomingAtom);

  return (
    <div
      className={flex({
        pos: 'relative',
        h: 110,
        alignItems: 'flex-end',
        textStyle: 'Text/M/Regular',
        color: 'content.default.level-3',
      })}
    >
      <span className={css({ w: { base: 76, tablet: 90 }, textAlign: 'left', tabletDown: { pos: 'absolute' } })}>
        <FormattedMessage id='Competition.r251215.Total_prize_pool_symbol' values={{ symbol: UsdtCurrency }} />
      </span>
      <div className={flex({ flexGrow: 1, justifyContent: 'space-between' })}>
        {milestones.map((milestone, index) => {
          const isFirst = index === 0;
          const isNext = index === currMilestoneIndex + 1;
          const isLast = index === milestones.length - 1;
          const dim = isUpcoming ? false : currMilestoneIndex === milestones.length - 1 ? !isLast : !isNext;
          const anchor = isFirst ? 'left' : isLast ? 'right' : 'center';
          const size = isFirst ? 'small' : isLast ? 'large' : 'medium';
          const showLockOnImage = !isUpcoming && isNext;
          const isShowMobile = isFirst || isLast || isNext || (isFinalStage && index === milestones.length - 2);

          return (
            <TopMilestone
              key={index}
              size={size}
              dim={dim}
              anchor={anchor}
              showLockOnImage={showLockOnImage}
              isFirst={isFirst}
              isShowMobile={isShowMobile}
            >
              <Formatted.ShortNumber value={milestone.prize} />
            </TopMilestone>
          );
        })}
      </div>
    </div>
  );
};

const Bottom = () => {
  const {
    milestonesAtom,
    currMilestoneIndexAtom,
    currentMilestoneValueAtom,
    isFinalStageAtom,
    isOngoingAtom,
    isUpcomingAtom,
  } = useBunja(milestoneBunja);
  const isOngoing = useAtomValue(isOngoingAtom);
  const isUpcoming = useAtomValue(isUpcomingAtom);
  const milestones = useAtomValue(milestonesAtom);
  const currMilestoneIndex = useAtomValue(currMilestoneIndexAtom);
  const currentMilestoneValue = useAtomValue(currentMilestoneValueAtom);
  const isFinalStage = useAtomValue(isFinalStageAtom);

  return (
    <div
      className={css(
        flex.raw({ pos: 'relative', textStyle: 'Text/M/Regular', color: 'content.default.level-3' }),
        isOngoing && { h: 80 },
      )}
    >
      <span className={css({ w: { base: 76, tablet: 90 }, textAlign: 'left', tabletDown: { pos: 'absolute' } })}>
        <FormattedMessage id='Competition.r251215.Total_volume_symbol' values={{ symbol: UsdtCurrency }} />
      </span>
      <div className={flex({ flexGrow: 1, justifyContent: 'space-between' })}>
        {milestones.map((milestone, index) => {
          const isFirst = index === 0;
          const isLast = index === milestones.length - 1;
          const isNext = index === currMilestoneIndex + 1;
          const leftValue = BigNumber.max(0, milestone.value.minus(currentMilestoneValue));
          const dim = isUpcoming ? false : currMilestoneIndex === milestones.length - 1 ? !isLast : !isNext;
          const anchor = isFirst ? 'left' : isLast ? 'right' : 'center';
          const isShowMobile = isFirst || isLast || isNext || (isFinalStage && index === milestones.length - 2);

          return (
            <BottomMilestone
              key={index}
              dim={dim}
              anchor={anchor}
              leftValue={leftValue}
              isFirst={isFirst}
              isShowMobile={isShowMobile}
            >
              <Formatted.ShortNumber value={milestone.value} />
            </BottomMilestone>
          );
        })}
      </div>
    </div>
  );
};

const Gauge = () => {
  const { gaugeProgressAtom, currentMilestoneValueAtom, isFinalStageAtom, isUpcomingAtom } = useBunja(milestoneBunja);
  const isUpcoming = useAtomValue(isUpcomingAtom);
  const gaugeProgress = useAtomValue(gaugeProgressAtom);
  const currentMilestoneValue = useAtomValue(currentMilestoneValueAtom);
  const isFinalStage = useAtomValue(isFinalStageAtom);

  const isZero = currentMilestoneValue.eq(0);

  return (
    <div
      className={flex({
        h: 16,
        rounded: 16,
        ml: { tablet: 90 },
        bgColor: 'background.accent.secondary.tint',
        '& > div:first-child > div:first-child': { roundedLeft: 16 },
        '& > div:last-child > div:first-child': { roundedRight: 16 },
      })}
    >
      {gaugeProgress.map((progress, index) => {
        const isLast = index === gaugeProgress.length - 1;
        const isPending = progress > 0 && progress < 100;
        const showLeftLine = !isUpcoming && ((isZero && index === 0) || isPending);
        const isShowMobile =
          (isZero && index === 0) || isPending || isLast || (isFinalStage && index >= gaugeProgress.length - 2);
        return (
          <GaugeFill
            progress={progress}
            showLeftLine={showLeftLine}
            isShowMobile={isShowMobile}
            css={{ flex: 1 }}
            key={index}
          />
        );
      })}
    </div>
  );
};

interface GaugeFillProps {
  progress?: number; // 0 ~ 1
  isShowMobile?: boolean;
  showLeftLine?: boolean;
  css?: Styles;
}
const GaugeFill = ({ css: cssProps, showLeftLine, isShowMobile, progress = 0 }: GaugeFillProps) => {
  return (
    <div
      data-show-mobile={isShowMobile || undefined}
      className={css({ pos: 'relative', h: '100%' }, !isShowMobile && { display: { tabletDown: 'none' } }, cssProps)}
    >
      <div
        className={css({
          h: '100%',
          overflow: 'hidden',
        })}
      >
        <div className={css({ h: '100%', bgColor: 'content.default.level-5' })} style={{ width: `${progress}%` }} />
      </div>
      {showLeftLine && (
        <div
          className={css({ pos: 'absolute', top: '50%', right: 0, transform: 'translate(50%, -50%)', lineHeight: 0 })}
        >
          <svg width='4' height='32' viewBox='0 0 4 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M2 2 L2 30' stroke='white' stroke-width='4' stroke-linecap='round' stroke-dasharray='2 6' />
          </svg>
        </div>
      )}
    </div>
  );
};

type TopMilestoneSize = 'small' | 'medium' | 'large';
type TopMilestoneAnchor = 'left' | 'center' | 'right';
interface TopMilestoneProps extends PropsWithChildren {
  anchor?: TopMilestoneAnchor;
  size: TopMilestoneSize;
  dim?: boolean;
  showLockOnImage?: boolean;
  isFirst: boolean;
  isShowMobile: boolean;
}
const TopMilestone = ({ anchor, size, dim, showLockOnImage, isFirst, isShowMobile, children }: TopMilestoneProps) => {
  const isLeft = anchor === 'left';
  const isRight = anchor === 'right';

  const milestoneIcon = {
    small: <img src={milestoneSIcon.src} alt='milestone-image' width={40} height={40} />,
    medium: <img src={milestoneMIcon.src} alt='milestone-image' width={60} height={60} />,
    large: <img src={milestoneLIcon.src} alt='milestone-image' width={60} height={80} />,
  }[size];

  return (
    <Place isShowMobile={isShowMobile}>
      <div
        className={css(
          vstack.raw({
            pos: 'absolute',
            bottom: 0,
            gap: 8,
            opacity: dim ? 0.5 : 1,
            transform: 'translateX(-50%)',
          }),
          isLeft && { alignItems: 'flex-start', transform: 'translateX(0)' },
          isRight && { alignItems: 'flex-end', transform: 'translateX(-100%)' },
          isFirst && { display: { base: 'none', tablet: 'flex' } },
        )}
      >
        <div className={css({ pos: 'relative' })}>
          {milestoneIcon}
          {showLockOnImage && (
            <div
              className={css({
                pos: 'absolute',
                bottom: -5,
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'rgba(0, 0, 0, 0.6)',
              })}
            >
              <LockClosedIcon size={24} />
            </div>
          )}
        </div>
        <span className={css({ textStyle: 'Text/M/Monospace/Strong', color: 'content.default.level-5' })}>
          {children}
        </span>
      </div>
    </Place>
  );
};

interface BottomMilestoneProps extends PropsWithChildren {
  anchor?: TopMilestoneAnchor;
  dim?: boolean;
  leftValue?: BigNumber;
  isFirst: boolean;
  isShowMobile: boolean;
}
const BottomMilestone = ({ anchor, dim, leftValue, isFirst, isShowMobile, children }: BottomMilestoneProps) => {
  const { isOngoingAtom } = useBunja(milestoneBunja);
  const isOngoing = useAtomValue(isOngoingAtom);

  const isLeft = anchor === 'left';
  const isRight = anchor === 'right';
  const isShowLeftValue = isOngoing && !dim && leftValue?.gt(0);

  return (
    <Place isShowMobile={isShowMobile}>
      <div
        className={css(
          vstack.raw({
            pos: 'absolute',
            top: 0,
            gap: 14,
            textStyle: 'Text/L/Monospace/Strong',
            color: 'content.default.level-5',
            opacity: dim ? 0.5 : 1,
            transform: 'translateX(-50%)',
          }),
          isLeft && { alignItems: 'flex-start', transform: 'translateX(0)' },
          isRight && { alignItems: 'flex-end', transform: 'translateX(-100%)' },
          isFirst && { display: { base: 'none', tablet: 'flex' } },
        )}
      >
        {children}
        {isShowLeftValue && (
          <div
            className={css(
              hstack.raw({
                pos: 'relative',
                textStyle: 'Text/L/Monospace/Strong',
                color: 'inverse.white',
                py: 8,
                px: 16,
                gap: 2,
                rounded: 1000,
                bgColor: 'var(--key-color)',
                _before: {
                  content: '""',
                  pos: 'absolute',
                  w: 0,
                  h: 0,
                  borderTop: '8px solid var(--key-color)',
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                },
              }),
              !isRight && { _before: { top: -7, right: '50%', transform: 'translateX(50%) rotate(180deg)' } },
              isRight && { roundedTopRight: 0, _before: { top: -4, right: -4, transform: 'rotate(90deg)' } },
            )}
          >
            <FormattedMessage
              id='Competition.r251217.volume_left'
              values={{ volume: <Formatted.ShortNumber value={leftValue ?? 0} precision={0} /> }}
            />
          </div>
        )}
      </div>
    </Place>
  );
};

interface PlaceProps extends PropsWithChildren {
  isShowMobile?: boolean;
}
const Place = ({ isShowMobile, children }: PlaceProps) => (
  <div className={css({ pos: 'relative', w: 0 }, !isShowMobile && { display: { tabletDown: 'none' } })}>{children}</div>
);
